import { useState } from "react";
import { creditedTo, likeCount, mmss, type TrackDoc } from "radio-shared";
import { api } from "../api";

/** État d'un import, dit en clair plutôt qu'en code d'erreur. */
function statusOf(track: TrackDoc): string | null {
  if (track.state === "importing") return "import…";
  if (track.state === "failed") return track.error ?? "raté";
  return null;
}

export function TrackList({
  tracks,
  empty,
  showTime,
  myUid,
  removable,
}: {
  tracks: TrackDoc[];
  empty: string;
  showTime?: boolean;
  myUid?: string;
  /** la liste « Déjà passé » ne le passe pas : aucun bouton n'y apparaît */
  removable?: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  const remove = async (trackId: string) => {
    setError(null);
    setPending(trackId);
    try {
      await api.removeTrack(trackId);
      // rien à faire de plus : le document disparaît, onSnapshot retire la ligne
    } catch (err) {
      setError(err instanceof Error ? err.message : "Retrait impossible");
    } finally {
      setPending(null);
    }
  };

  if (tracks.length === 0) return <p className="muted">{empty}</p>;

  return (
    <>
      <ol className="tracks">
        {tracks.map((track) => {
          const status = statusOf(track);
          const mine = removable && myUid !== undefined && track.addedByUid === myUid;
          const likes = likeCount(track.likes);

          return (
            <li key={track.id} className={`tracks__item${track.state === "failed" ? " tracks__item--failed" : ""}`}>
              <span className="tracks__kind">
                {track.kind === "voice" ? "🎙️" : track.kind === "youtube" ? "▶" : "♫"}
              </span>
              <span className="tracks__title">
                {track.title}
                {track.uploader && <span className="tracks__sub"> — {track.uploader}</span>}
              </span>
              <span className="tracks__meta">
                {status ?? (showTime && track.durationMs > 0 ? mmss(track.durationMs) : "")}
              </span>
              <span className="tracks__by">
                {/* Une rediffusion ne nomme personne : la radio est allée la
                    rechercher toute seule, elle dit d'où elle sort. */}
                {creditedTo(track) ?? <span className="tracks__rerun">rediffusion</span>}
                {/* Les cœurs restent attachés au son : on les retrouve ici, et
                    une rediffusion ne les remet pas en jeu. */}
                {likes > 0 && (
                  <span className="tracks__likes" title={`${likes} cœur${likes > 1 ? "s" : ""}`}>
                    ♥ {likes}
                  </span>
                )}
                {mine && (
                  <button
                    className="tracks__remove"
                    type="button"
                    title="retirer mon son de la file"
                    disabled={pending === track.id}
                    onClick={() => void remove(track.id)}
                  >
                    ✕
                  </button>
                )}
              </span>
            </li>
          );
        })}
      </ol>
      {error && <p className="tracks__error">{error}</p>}
    </>
  );
}
